import { Injectable } from "@angular/core";
import * as forge from "node-forge";

@Injectable({
    providedIn: "root",
})
export class CryptoService {
    private readonly algorithm = "AES-CBC";
    private readonly keySize = 32; // 256 bits
    private readonly ivSize = 16; // 128 bits for CBC

    // Cache for the encryption key
    private encryptionKey: string | null = null;

    constructor() {
    }

    /**
     * Generates or retrieves the encryption key for this browser session
     * Key is derived from a combination of browser fingerprint and session data
     */
    private getEncryptionKey(): string {
        if (this.encryptionKey) {
            return this.encryptionKey;
        }

        // Create browser fingerprint components
        const fingerprint = [
            navigator.userAgent,
            navigator.language,
            screen.width + "x" + screen.height,
            Intl.DateTimeFormat().resolvedOptions().timeZone,
            new Date().getTimezoneOffset().toString(),
            // Add session-specific entropy
            "jaak-nfury-storage-key-v2-forge",
            window.location.origin,
        ].join("|");

        // Hash the fingerprint to create consistent key material using forge
        const md = forge.md.sha256.create();
        md.update(fingerprint);
        this.encryptionKey = md.digest().getBytes();

        return this.encryptionKey;
    }

    /**
     * Encrypts a value using AES-CBC with node-forge
     * @param value - The value to encrypt (will be JSON stringified)
     * @returns Base64 encoded encrypted data with IV prepended
     */
    public encrypt(value: any): string {
        try {
            const key = this.getEncryptionKey();

            // Convert value to JSON string
            const jsonString = JSON.stringify(value);

            // Convert to Base64 to avoid encoding issues with special characters
            const base64Data = forge.util.encode64(jsonString);

            // Generate random IV
            const iv = forge.random.getBytesSync(this.ivSize);

            // Create cipher and encrypt the base64 string
            const cipher = forge.cipher.createCipher(this.algorithm, key);
            cipher.start({ iv });
            cipher.update(forge.util.createBuffer(base64Data));
            cipher.finish();

            // Combine IV + encrypted data
            const encrypted = cipher.output.getBytes();
            const combined = iv + encrypted;

            // Convert to base64 for storage
            return forge.util.encode64(combined);
        } catch (error) {
            console.error("Encryption failed:", error);
            // Fallback: return original value as JSON (for development/debugging)
            return JSON.stringify(value);
        }
    }

    /**
     * Decrypts a value that was encrypted with encrypt()
     * @param encryptedValue - Base64 encoded encrypted data
     * @returns The original decrypted value
     */
    public decrypt<T = any>(encryptedValue: string): T | null {
        try {
            if (!encryptedValue) {
                return null;
            }

            // Check if the value is already decrypted JSON (fallback case for legacy data)
            if (!this.isBase64(encryptedValue)) {
                try {
                    return JSON.parse(encryptedValue);
                } catch {
                    return null;
                }
            }

            const key = this.getEncryptionKey();

            // Convert from base64 - wrap in try-catch to handle malformed data
            let combined: string;
            try {
                combined = forge.util.decode64(encryptedValue);
            } catch (decodeError) {
                console.warn("Base64 decode failed, attempting to parse as plain JSON:", decodeError);
                try {
                    return JSON.parse(encryptedValue);
                } catch {
                    return null;
                }
            }

            // Extract IV and encrypted data
            const iv = combined.substring(0, this.ivSize);
            const encrypted = combined.substring(this.ivSize);

            // Create decipher
            const decipher = forge.cipher.createDecipher(this.algorithm, key);
            decipher.start({ iv });
            decipher.update(forge.util.createBuffer(encrypted));

            if (!decipher.finish()) {
                throw new Error("Decryption failed - invalid data or key");
            }

            // Get decrypted data (which is base64 encoded)
            let base64Data: string;
            try {
                base64Data = decipher.output.toString();
            } catch (toStringError) {
                console.error("Failed to convert decrypted buffer to string (URI malformed):", toStringError);
                throw new Error("Invalid decrypted data - possibly corrupted or wrong key");
            }

            // Decode from Base64 to get original JSON string
            let jsonString: string;
            try {
                jsonString = forge.util.decode64(base64Data);
            } catch (base64Error) {
                console.error("Failed to decode base64 after decryption:", base64Error);
                // This might be old data that wasn't base64 encoded before encryption
                // Try using base64Data directly as JSON
                jsonString = base64Data;
            }

            // Parse JSON and return
            return JSON.parse(jsonString);
        } catch (error) {
            console.error("Decryption failed:", error);

            // Try to parse as plain JSON (fallback for unencrypted legacy data)
            try {
                return JSON.parse(encryptedValue);
            } catch {
                return null;
            }
        }
    }

    /**
     * Checks if node-forge is available and working
     */
    public isSupported(): boolean {
        try {
            // Test if forge is available and can create a cipher
            forge.cipher.createCipher("AES-CBC", "test-key-32-bytes-long-for-testing");
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Simple check if string looks like base64
     */
    private isBase64(str: string): boolean {
        try {
            return forge.util.encode64(forge.util.decode64(str)) === str;
        } catch {
            return false;
        }
    }

    /**
     * Clear the cached encryption key (useful for testing or security)
     */
    public clearKey(): void {
        this.encryptionKey = null;
    }

    /**
     * Generate a random key for testing purposes
     */
    public generateRandomKey(): string {
        return forge.random.getBytesSync(this.keySize);
    }

    /**
     * Hash a string using SHA-256
     */
    public hashString(input: string): string {
        const md = forge.md.sha256.create();
        md.update(input);
        return forge.util.encode64(md.digest().getBytes());
    }
}
